import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaChevronDown,
  FaSearch,
  FaBars,
  FaTimes,
  FaChevronRight,
} from "react-icons/fa";
import logo from "../../assets/jriit-logo-cropped.png";
import SmartSearch from "./SmartSearch";

const Navbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSmartSearchOpen, setIsSmartSearchOpen] = useState(false);

  // Refs for dropdown containers
  const aboutDropdownRef = useRef(null);
  const coursesDropdownRef = useRef(null);
  const galleryDropdownRef = useRef(null);
  const newsDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Desktop hover timers for delayed hide
  const hoverTimers = useRef({});

  const showDropdown = (dropdown) => {
    // Clear any pending hide timers for this dropdown
    if (hoverTimers.current[dropdown]) {
      clearTimeout(hoverTimers.current[dropdown]);
      hoverTimers.current[dropdown] = null;
    }
    setActiveDropdown(dropdown);
  };

  const scheduleHideDropdown = (dropdown, delay = 350) => {
    if (hoverTimers.current[dropdown])
      clearTimeout(hoverTimers.current[dropdown]);
    hoverTimers.current[dropdown] = setTimeout(() => {
      // Only hide if the currently open dropdown matches
      setActiveDropdown((current) => (current === dropdown ? null : current));
      hoverTimers.current[dropdown] = null;
    }, delay);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setActiveMobileDropdown(null); // Close any open mobile dropdowns when menu closes
  };

  const toggleMobileDropdown = (dropdown) => {
    setActiveMobileDropdown(
      activeMobileDropdown === dropdown ? null : dropdown,
    );
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setIsSmartSearchOpen(!isSmartSearchOpen);
    if (!isSearchOpen) {
      setSearchTerm("");
    }
  };

  const closeSmartSearch = () => {
    setIsSmartSearchOpen(false);
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.assign(`/news?query=${encodeURIComponent(searchTerm)}`);
    }
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check desktop dropdowns
      if (
        aboutDropdownRef.current &&
        !aboutDropdownRef.current.contains(event.target) &&
        coursesDropdownRef.current &&
        !coursesDropdownRef.current.contains(event.target) &&
        galleryDropdownRef.current &&
        !galleryDropdownRef.current.contains(event.target) &&
        newsDropdownRef.current &&
        !newsDropdownRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }

      // Check mobile menu
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        // Don't close mobile menu if clicking on the menu button itself
        const menuButton = event.target.closest(".mobile-menu-button");
        if (!menuButton) {
          setIsMobileMenuOpen(false);
          setActiveMobileDropdown(null);
        }
      }
    };

    // Handle keyboard shortcuts
    const handleKeyDown = (event) => {
      // Ctrl+K or Cmd+K for search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        toggleSearch();
      }
      // Escape to close search
      if (event.key === 'Escape' && isSmartSearchOpen) {
        closeSmartSearch();
      }
    };

    // Add event listeners
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSmartSearchOpen]);

  return (
    <header className="sticky top-0 z-50 animate-slide-down">
      {/* Desktop Navigation Bar */}
      <div className="bg-white p-4 h-32 items-center relative shadow-lg hidden xl:flex">
        <div className="text-custom-pink font-bold text-1xl flex items-center">
          <img
            src={logo}
            alt="logo"
            className="mr-2 ml-6 w-24 h-24 object-contain xl:hidden 2xl:block"
          />
          <span className="ml-4 xl:ml-0 2xl:ml-4 flex-1 text-center max-w-xs tracking-wider text-sm xl:text-base 2xl:text-sm 3xl:text-lg">
            JR INSTITUTE OF INFORMATION TECHNOLOGY
            <span className="block border-b border-gold px-2 py-1"></span>
            <span className="block text-xs xl:text-sm 2xl:text-xs py-2 text-center tracking-widest">
              ELIMU NA UJUZI
            </span>
          </span>
        </div>

        <div className="headerRightBottomWrapper absolute top-0 right-0 w-9/12 flex flex-col">
          <div className="rounded-bl-xl bg-custom-pink p-2 flex justify-between items-center">
            <div className="headerBottomLinks flex space-x-12 ml-4">
              <Link to="/academics" className="text-white hover:text-gold">
                Academics
              </Link>
              <Link to="/departments" className="text-white hover:text-gold">
                Departments
              </Link>
              <Link to="/programs" className="text-white hover:text-gold">
                Programs
              </Link>
              <Link to="/library" className="text-white hover:text-gold">
                Library
              </Link>
              <Link to="/services" className="text-white hover:text-gold">
                Services
              </Link>
              <Link to="/athletics" className="text-white hover:text-gold">
                Athletics
              </Link>
              <Link to="/feedback" className="text-white hover:text-gold">
                Feedback
              </Link>
              <Link to="/campus-tour" className="text-white hover:text-gold">
                Campus Tour
              </Link>
            </div>
          </div>

          <div className="bg-white p-6 flex justify-between items-center">
            <div className="main-nav-links flex space-x-12 xl:space-x-8 2xl:space-x-12 text-center">
              <Link to="/" className="relative group text-gray-700">
                Home
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>

              {/* About Us Dropdown */}
              <div
                className="relative"
                ref={aboutDropdownRef}
                onMouseEnter={() => showDropdown("about")}
                onMouseLeave={() => scheduleHideDropdown("about", 400)}
              >
                <Link
                  to="/about"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  About Us
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "about" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "about" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/about"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Our History
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/about/mission"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Mission & Vision
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/about/trustees"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Advasory Board
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/about/terms-conditions"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Terms and Conditions
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </div>
                )}
              </div>

              {/* Courses Dropdown */}
              <div
                className="relative"
                ref={coursesDropdownRef}
                onMouseEnter={() => showDropdown("courses")}
                onMouseLeave={() => scheduleHideDropdown("courses", 400)}
              >
                <Link
                  to="/course/information-technology"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  Courses
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "courses" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "courses" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/course/information-technology"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Information Technology
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/course/cyber-security"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Cybersecurity
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/course/business-administration"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Business Administration
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/course/travel-tourism-management"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Travel & Tourism Management
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/course/professional-courses"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Professional Course
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/course/short-courses"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Short Courses
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </div>
                )}
              </div>

              {/* Gallery Dropdown */}
              <div
                className="relative"
                ref={galleryDropdownRef}
                onMouseEnter={() => showDropdown("gallery")}
                onMouseLeave={() => scheduleHideDropdown("gallery", 400)}
              >
                <Link
                  to="/gallery/photos"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  Gallery
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "gallery" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "gallery" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/gallery/photos"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Photo Gallery
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/gallery/videos"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Video Gallery
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                  </div>
                )}
              </div>

              {/* News & Events Dropdown */}
              <div
                className="relative"
                ref={newsDropdownRef}
                onMouseEnter={() => showDropdown("news")}
                onMouseLeave={() => scheduleHideDropdown("news", 400)}
              >
                <Link
                  to="/news"
                  className="relative group text-gray-700 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                >
                  News & Events
                  <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
                  <FaChevronDown
                    className={`ml-2 w-4 h-4 transition-transform duration-200 ${activeDropdown === "news" ? "rotate-180" : ""
                      }`}
                  />
                </Link>
                {activeDropdown === "news" && (
                  <div className="absolute left-0 top-full bg-white shadow-lg mt-2 rounded w-72 z-50 text-left transition-opacity duration-200 ease-out opacity-100">
                    <Link
                      to="/events"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      All Events
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    <Link
                      to="/news"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      All News
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link>
                    {/*                    <Link
                      to="/blog"
                      className="group flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Blog
                      <FaChevronRight className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                    </Link> */}
                  </div>
                )}
              </div>

              <Link to="/admissions" className="relative group text-gray-700">
                Admissions
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/fees" className="relative group text-gray-700">
                Fee Structure
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/contact" className="relative group text-gray-700">
                Contact Us
                <span className="absolute -bottom-4 left-0 w-0 h-[2px] bg-gold transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  onClick={toggleSearch}
                  className="group relative overflow-hidden rounded-2xl bg-custom-pink/10 backdrop-blur-xl border border-custom-pink/20 px-4 py-2 transition-all duration-300 hover:bg-custom-pink/20 hover:scale-105 hover:shadow-2xl hover:shadow-custom-pink/10 active:scale-95"
                  style={{
                    background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(147, 51, 234, 0.05) 100%)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(147, 51, 234, 0.2)',
                  }}
                >
                  {/* Liquid Glass Effect */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"
                    style={{
                      background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(147, 51, 234, 0.1) 50%, rgba(147, 51, 234, 0.05) 100%)',
                    }}
                  />
                  
                  {/* Content */}
                  <div className="relative flex items-center space-x-2 text-custom-pink">
                    <FaSearch className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                    <span className="text-sm font-medium hidden lg:block xl:hidden 2xl:block transition-all duration-300 group-hover:text-gold">
                      Ctrl+K
                    </span>
                  </div>
                  
                  {/* Shimmer Effect */}
                  <div 
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.3), transparent)',
                    }}
                  />
                </button>
                
                {isSmartSearchOpen && (
                  <SmartSearch
                    isOpen={isSmartSearchOpen}
                    onClose={closeSmartSearch}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                  />
                )}
              </div>
              
              <a
                href="https://apply.jrsys.ac.tz/"
                target="_blank"
                rel="noreferrer"
                className="apply-button bg-gold text-white px-4 py-2 rounded-3xl hover:bg-custom-pink"
              >
                Apply Now
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Bar */}
      <div className="xl:hidden flex justify-between items-center bg-custom-pink text-white p-4">
        <img
          src={logo}
          alt="logo"
          className="w-24 h-24 object-contain xl:hidden"
        />
        <div className="hidden md:block xl:hidden flex-1 text-center">
          <div className="text-sm font-bold tracking-wider">
            JR INSTITUTE OF INFORMATION TECHNOLOGY
          </div>
          <div className="flex justify-center my-1">
            <div className="border-b border-gold w-16"></div>
          </div>
          <div className="text-xs tracking-widest">ELIMU NA UJUZI</div>
        </div>
        <div className="flex items-center space-x-4">
          <a
            href="https://apply.jrsys.ac.tz/"
            target="_blank"
            rel="noreferrer"
            className="hidden md:block 2xl:hidden bg-gold text-white px-3 py-1.5 rounded-full text-sm hover:bg-custom-pink transition-colors"
          >
            Apply Now
          </a>
          <div
            onClick={toggleMobileMenu}
            className="mobile-menu-button cursor-pointer text-3xl space-x-2 flex items-center"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
            <span className="text-lg ">
              {isMobileMenuOpen ? "Close" : "Menu"}
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="xl:hidden bg-white text-left p-4 space-y-2 shadow-md"
          ref={mobileMenuRef}
        >
          <Link
            to="/"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Home
          </Link>

          {/* Mobile About Us Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("about")}
            >
              About Us
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "about" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "about" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link to="/about" className="block text-gray-600 py-1 text-sm">
                  Our History
                </Link>
                <Link
                  to="/about/mission"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Mission & Vision
                </Link>
                <Link
                  to="/about/trustees"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Advasory Board
                </Link>
                <Link
                  to="/about/terms-conditions"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Terms and Conditions
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Courses Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("courses")}
            >
              Courses
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "courses" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "courses" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link
                  to="/course/information-technology"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Information Technology
                </Link>
                <Link
                  to="/course/cyber-security"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Cybersecurity
                </Link>
                <Link
                  to="/course/business-administration"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Business Administration
                </Link>
                <Link
                  to="/course/travel-tourism-management"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Travel & Tourism Management
                </Link>
                <Link
                  to="/course/professional-courses"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Professional Course
                </Link>
                <Link
                  to="/course/short-courses"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Short Courses
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Gallery Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("gallery")}
            >
              Gallery
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "gallery" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "gallery" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link
                  to="/gallery/photos"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Photo Gallery
                </Link>
                <Link
                  to="/gallery/videos"
                  className="block text-gray-600 py-1 text-sm"
                >
                  Video Gallery
                </Link>
              </div>
            )}
          </div>

          {/* Mobile News & Events Dropdown */}
          <div>
            <div
              className="flex items-center justify-between text-gray-700 py-2 border-b border-gray-200 cursor-pointer"
              onClick={() => toggleMobileDropdown("news")}
            >
              News & Events
              <FaChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${activeMobileDropdown === "news" ? "rotate-180" : ""
                  }`}
              />
            </div>
            {activeMobileDropdown === "news" && (
              <div className="ml-4 mt-2 space-y-1">
                <Link to="/events" className="block text-gray-600 py-1 text-sm">
                  All Events
                </Link>
                <Link to="/news" className="block text-gray-600 py-1 text-sm">
                  All News
                </Link>
                <Link to="/blog" className="block text-gray-600 py-1 text-sm">
                  Blog
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/admissions"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Admissions
          </Link>
          <Link
            to="/fees"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Fee Structure
          </Link>
          <Link
            to="/athletics"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Athletics
          </Link>
          <Link
            to="/campus-tour"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Campus Tour
          </Link>
          <Link
            to="/contact"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Contact Us
          </Link>
          <Link
            to="/academics"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Academics
          </Link>
          <Link
            to="/departments"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Departments
          </Link>
          <Link
            to="/programs"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Programs
          </Link>
          <Link
            to="/library"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Library
          </Link>
          <Link
            to="/services"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Services
          </Link>
          <Link
            to="/feedback"
            className="block text-gray-700 py-2 border-b border-gray-200"
          >
            Feedback
          </Link>

          {/* Mobile Search */}
          <div className="py-2 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search courses, pages, and more..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
              />
              <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            {searchTerm.trim() && (
              <div className="mt-2 text-xs text-gray-500">
                Press Enter to search or use Ctrl+K for advanced search
              </div>
            )}
          </div>

          <a
            href="https://apply.jrsys.ac.tz/"
            target="_blank"
            rel="noreferrer"
            className="block bg-gold text-white py-3 rounded-full text-center mt-4"
          >
            Apply Now
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
